import {Trans} from '@lingui/macro';
import {chain, concat} from 'lodash';
import React, {FC, Fragment, useCallback, useMemo, useState} from 'react';
import styled from 'styled-components';

import {DropdownItemContent} from '../../../../libs/shared/ui/src/components/dropdowns/dropdownItemContent';
import {TagsMap} from '../../../analytics/components/filters/common/tagSelectionDropdown';
import {buildTagSearchUrl, SearchTagStatusesEnum} from '../../../helpers/models/tagHelpers';
import {useAppSelector} from '../../../helpers/reduxHelpers';
import {CompanyModel} from '../../../models/api/companyModel';
import {ApiSearchTags, SearchTagModel} from '../../../models/api/searchTagModel';
import {TagBehaviorsEnum, TagModel} from '../../../models/api/tagModel';
import {fetchApiAsync} from '../../../services/http/http';
import {getCompany} from '../../../store/data/company/companySelectors';
import {DropdownItemTagIcon} from '../../common/dropdowns/dropdownItemTagIcon';
import {DropdownFetcher} from '../../common/dropdowns/source/asyncDropdownSource';
import {
  maybeRenderDeduplicationIcon,
  renderFullTagName,
  renderTagNameInTooltip,
} from '../../common/dropdowns/tagDropdownItem';
import {FilterRowGenerator} from '../../common/filters/resourceMultiSelectFilterDropdown';
import {computeFilterDropdownSourceAsync} from '../../common/filters/sourceFilter/filterDropdownSource';
import {ResourceMultiSelectInput} from '../../common/resourceMultiSelectInput/resourceMultiSelectInput';
import {LensFormMultiselectProps} from './types';

// Starred Tags are personal tags, but this component is only intended for team tags at this time.
const isStarredTag = false;

/*
 * Props.
 */

interface LensFormMultiselectInboxesProps extends Omit<LensFormMultiselectProps<TagModel>, 'choices'> {
  namespace: string | undefined;
}

/*
 * Style.
 */

const StyledContentDiv = styled.div`
  display: flex;
  align-items: center;
  max-width: 100%;
`;

/*
 * Component.
 */

export const LensFormMultiSelectTags: FC<LensFormMultiselectInboxesProps> = (props) => {
  const {isDisabled, selectedIds, onSetItemIds, namespace} = props;

  const company = useAppSelector(getCompany);

  const dropdownSource = useMemo(() => {
    const fetcher = buildFetcher(company, namespace);
    return computeFilterDropdownSourceAsync(fetcher);
  }, [company, namespace]);

  // Tags that are available for using to render the dropdown button value.
  //  These are tags that have been selected at some point and may or may not be currently selected.
  //  We only store these to guarantee we can show the name of selected tags as the button value.
  const [tagsMap, setTagsMap] = useState<TagsMap>({});

  const buildRowGenerator: FilterRowGenerator<SearchTagModel> = useCallback(
    (searchTag, dropdownSelectedIds, makeOnItemSelect, isSearching) => {
      // Tag search returns an array of tags. The array represents the tag and its parent tags,
      // with the child tag coming first in the array. The request returns every tag, regardless
      // of its place in the hierarchy.
      const tag = searchTag[0];
      const isChecked = dropdownSelectedIds.includes(tag.id);

      const onItemSelect = makeOnItemSelect(tag.id);
      const onSelect = () => {
        setTagsMap((prevTagsMap) => ({
          ...prevTagsMap,
          [tag.id]: tag,
        }));
        onItemSelect();
      };

      const nestingLevel = isSearching ? 0 : searchTag.length - 1;
      // If we are searching we display the tag + it's path on the same line,
      // and when not searching we display a tree nested view.
      const tagsPath = isSearching ? searchTag : [];
      const renderTag = () => (
        <>
          <DropdownItemTagIcon tag={tag} nestingLevel={nestingLevel} />
          <DropdownItemContent isChecked={isChecked}>
            <StyledContentDiv>
              {renderFullTagName(tag, tagsPath, isStarredTag)}
              {maybeRenderDeduplicationIcon(tag)}
            </StyledContentDiv>
          </DropdownItemContent>
        </>
      );

      return {
        renderer: renderTag,
        onSelect,
        isSelected: isChecked,
        renderTooltip: () => renderTagNameInTooltip(tag, tagsPath),
      };
    },
    [setTagsMap],
  );

  return (
    <ResourceMultiSelectInput
      dropdownSource={dropdownSource}
      selectedItemIds={selectedIds}
      computeFilterGenerator={buildRowGenerator}
      onSetItems={onSetItemIds}
      onClearAll={() => onSetItemIds([])}
      renderValue={() => renderValue(props, tagsMap)}
      renderPlaceholder={() => <Trans id="lens-form-multiselect-tags-placeholder">Select team tags</Trans>}
      label={<Trans id="lens-form-multiselect-tags-label">With at least one of these tags</Trans>}
      dropdownTitle={<Trans id="lens-form-multiselect-tags-dropdown-title">Team tags</Trans>}
      isDisabled={isDisabled}
      isRequired={false}
    />
  );
};

/*
 * Helpers.
 */

function renderValue(props: LensFormMultiselectInboxesProps, tagsMap: TagsMap) {
  const {selectedIds} = props;

  if (!selectedIds.length) {
    return null;
  }

  const selectedTags = chain(selectedIds)
    .map((id) => tagsMap[id])
    .compact()
    .value();

  const lastIndex = selectedTags.length - 1;
  // TODO-LENS: render icon/emoji before name.
  return (
    <>
      {selectedTags.map((tag, index) => (
        <Fragment key={tag.id}>
          {tag.name}
          {index !== lastIndex && ', '}
        </Fragment>
      ))}
    </>
  );
}

function buildFetcher(company: CompanyModel, namespace: string | undefined): DropdownFetcher<SearchTagModel> {
  return async (query, tags, cancelToken) => {
    if (!namespace) {
      return {
        items: [],
        canLoadMore: false,
      };
    }

    // Exclude initial items to compute the offset.
    const existingCount = tags.filter((tag) => tag).length;

    const url = buildTagSearchUrl(company, query, {
      namespaces: [namespace],
      offset: existingCount,
      // TODO-LENS: verify behaviors for this search request.
      behaviors: [TagBehaviorsEnum.CAN_DISPLAY],
      status: SearchTagStatusesEnum.ACTIVE,
    });

    const searchResults = await fetchApiAsync(ApiSearchTags, url, {cancelToken});

    return {
      items: concat(tags, searchResults.tags),
      canLoadMore: searchResults.tags.length === searchResults.limit,
    };
  };
}
